import { proxy, subscribe, useSnapshot } from 'valtio'
import { devtools } from 'valtio/utils'

import localStorage from '@/utils/local-storage'

/**
 * Auto-scroll on/off per rundown, keyed by rundown id.
 *
 * @remarks
 *   Persisted to localStorage under `autoScroll`, the key and shape the Jotai
 *   atom used before, so existing users keep their settings. A rundown with no
 *   entry is on (`AUTO_SCROLL_DEFAULT`), for the editor and the active marker
 *   alike.
 */
type AutoScrollState = Record<string, boolean>

const STORAGE_KEY = 'autoScroll'

/** Auto-scroll for a rundown that has never set it. */
const AUTO_SCROLL_DEFAULT = true

/**
 * Parse the stored map. Anything that is not a JSON object, including `null`
 * (no entry), reads as empty.
 */
function parseStored(raw: string | null | undefined): AutoScrollState {
  if (!raw) return {}

  try {
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as AutoScrollState)
      : {}
  } catch {
    return {}
  }
}

const autoScrollStore = proxy<AutoScrollState>(
  parseStored(localStorage.getItem(STORAGE_KEY)),
)

// Writes the whole map. Valtio batches the subscription, so a burst of
// changes is one write.
subscribe(autoScrollStore, () => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(autoScrollStore))
})

// Other tabs changing the setting: adopt the whole map, as the Jotai atom did
// through `atomWithStorage`. `key` is null when another tab cleared all of
// storage, and `newValue` is null when the key was removed; both mean empty.
// Setting the same string again fires no event, so this does not loop.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.storageArea !== window.localStorage) return
    if (event.key !== STORAGE_KEY && event.key !== null) return

    const next = parseStored(event.newValue)
    for (const rundownId of Object.keys(autoScrollStore)) {
      if (!(rundownId in next))
        Reflect.deleteProperty(autoScrollStore, rundownId)
    }
    Object.assign(autoScrollStore, next)
  })
}

export const autoScrollActions = {
  /** Turn auto-scroll on or off for one rundown. */
  setAutoScroll(rundownId: string, enabled: boolean) {
    autoScrollStore[rundownId] = enabled
  },
}

/**
 * The auto-scroll setting of one rundown, `AUTO_SCROLL_DEFAULT` if never set.
 *
 * Narrow: only re-renders when this rundown's entry changes.
 */
export function useAutoScroll(rundownId: string): boolean {
  return useSnapshot(autoScrollStore)[rundownId] ?? AUTO_SCROLL_DEFAULT
}

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(autoScrollStore, { name: 'autoScrollStore', enabled: true })
}
// #endregion
